/**
 * Wells Wallet – Professional Skeleton Loading System
 * 
 * Each skeleton precisely mirrors the real page layout for both desktop and mobile
 * to eliminate layout shift and deliver a seamless perceived-performance experience.
 * 
 * Primitives use a smooth multi-stop shimmer animation defined in index.css.
 */

import { type CSSProperties, type ReactNode } from 'react'

// ─── Primitives ─────────────────────────────────────────────────────────────────

/** Shimmer bone – a rounded rectangle with a sweeping highlight. */
function Bone({
    className = '',
    style,
    delay = 0,
}: {
    className?: string
    style?: CSSProperties
    delay?: number
}) {
    return (
        <div
            className={`relative overflow-hidden rounded bg-white/[0.06] ${className}`}
            style={{
                ...style,
                animationDelay: delay ? `${delay}ms` : undefined,
            }}
        >
            <div
                className="absolute inset-0 shimmer"
                style={delay ? { animationDelay: `${delay}ms` } : undefined}
            />
        </div>
    )
}

/** Icon-sized shimmer block (rounded-xl by default). */
function BoneIcon({
    size = 40,
    radius = 'rounded-xl',
    className = '',
    delay = 0,
}: {
    size?: number
    radius?: string
    className?: string
    delay?: number
}) {
    return (
        <div
            className={`relative overflow-hidden flex-shrink-0 ${radius} bg-white/[0.06] ${className}`}
            style={{ width: size, height: size }}
        >
            <div
                className="absolute inset-0 shimmer"
                style={delay ? { animationDelay: `${delay}ms` } : undefined}
            />
        </div>
    )
}

/** Wrapper that adds a soft fade-in to skeleton groups. */
function Wrap({ children, className = '' }: { children: ReactNode; className?: string }) {
    return <div className={`animate-fadeIn ${className}`}>{children}</div>
}

/** A settings-style list item: icon + label + trailing. */
function BoneListRow({ delay = 0 }: { delay?: number }) {
    return (
        <div className="flex items-center gap-3 px-4 py-4">
            <BoneIcon size={40} radius="rounded-xl" delay={delay} />
            <Bone className="flex-1 h-4 rounded" delay={delay + 50} />
            <Bone className="h-4 w-14 rounded" delay={delay + 100} />
        </div>
    )
}

// ─── Dashboard Skeleton ─────────────────────────────────────────────────────────

export function DashboardSkeleton() {
    return (
        <Wrap>
            {/* ── Header ─ matches Dashboard header exactly ── */}
            <header className="px-5 lg:px-8 pt-5 lg:pt-6 pb-3">
                <div className="flex items-center justify-between max-w-5xl mx-auto">
                    <div className="flex items-center gap-3 lg:gap-4">
                        {/* Mobile avatar */}
                        <div
                            className="lg:hidden w-14 h-14 rounded-2xl overflow-hidden relative"
                            style={{
                                background: 'linear-gradient(135deg, rgba(99,102,241,0.25), rgba(139,92,246,0.2))',
                                border: '2px solid rgba(255,255,255,0.1)',
                            }}
                        >
                            <div className="absolute inset-0 shimmer" />
                        </div>
                        <div className="space-y-2">
                            <Bone className="h-3.5 w-28 rounded" delay={50} />
                            <Bone className="h-5 w-20 rounded" delay={100} />
                        </div>
                    </div>
                    <div className="flex items-center gap-2 lg:gap-3">
                        <BoneIcon size={44} radius="rounded-xl" className="lg:!w-12 lg:!h-12" delay={150} />
                        <BoneIcon size={44} radius="rounded-xl" className="lg:!w-12 lg:!h-12" delay={200} />
                    </div>
                </div>
            </header>

            {/* ── Content ── */}
            <div className="px-5 lg:px-8">
                <div className="max-w-5xl mx-auto">
                    <div className="lg:grid lg:grid-cols-5 lg:gap-6">
                        {/* Left Column */}
                        <div className="lg:col-span-3 space-y-5 lg:space-y-6">
                            {/* Balance Cards – 2×1 grid */}
                            <div className="grid grid-cols-2 gap-3 lg:gap-4">
                                {/* Mind Gems */}
                                <div
                                    className="p-4 lg:p-6 rounded-2xl"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(139,92,246,0.15), rgba(168,85,247,0.1))',
                                        border: '1px solid rgba(139,92,246,0.3)',
                                    }}
                                >
                                    <div className="flex items-center gap-2 mb-3">
                                        <BoneIcon size={32} radius="rounded-lg" className="lg:!w-10 lg:!h-10" delay={100} />
                                        <Bone className="h-3 w-16 rounded" delay={150} />
                                    </div>
                                    <Bone className="h-7 lg:h-8 w-24 rounded mb-2" delay={200} />
                                    <Bone className="h-3.5 w-28 rounded" delay={250} />
                                </div>
                                {/* Fluxon */}
                                <div
                                    className="p-4 lg:p-6 rounded-2xl"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(234,179,8,0.15), rgba(245,158,11,0.1))',
                                        border: '1px solid rgba(234,179,8,0.3)',
                                    }}
                                >
                                    <div className="flex items-center gap-2 mb-3">
                                        <BoneIcon size={32} radius="rounded-lg" className="lg:!w-10 lg:!h-10" delay={150} />
                                        <Bone className="h-3 w-12 rounded" delay={200} />
                                    </div>
                                    <Bone className="h-7 lg:h-8 w-24 rounded mb-2" delay={250} />
                                    <Bone className="h-3.5 w-28 rounded" delay={300} />
                                </div>
                            </div>

                            {/* Quick Actions – mobile only */}
                            <div className="lg:hidden grid grid-cols-4 gap-2">
                                {[0, 1, 2, 3].map((i) => (
                                    <div key={i} className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-white/[0.03]">
                                        <BoneIcon size={48} radius="rounded-2xl" delay={300 + i * 60} />
                                        <Bone className="h-3 w-10 rounded" delay={360 + i * 60} />
                                    </div>
                                ))}
                            </div>

                            {/* Wallet/Exchange Tab Pair */}
                            <div className="flex gap-2 lg:gap-3">
                                <Bone
                                    className="flex-1 h-12 lg:h-14 rounded-xl"
                                    delay={400}
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(99,102,241,0.18), rgba(124,58,237,0.12))',
                                    }}
                                />
                                <Bone className="flex-1 h-12 lg:h-14 rounded-xl" delay={440} />
                            </div>

                            {/* Recent Activity */}
                            <div>
                                <div className="flex items-center justify-between mb-3 lg:mb-4">
                                    <Bone className="h-4 w-28 rounded" delay={500} />
                                    <Bone className="h-3.5 w-14 rounded" delay={530} />
                                </div>
                                <div className="rounded-2xl p-3 lg:p-4 bg-white/[0.02] border border-white/5">
                                    {[0, 1, 2, 3, 4].map((i) => (
                                        <div
                                            key={i}
                                            className={`flex items-center gap-3 py-3 ${i < 4 ? 'border-b border-white/5' : ''}`}
                                        >
                                            <BoneIcon size={44} radius="rounded-2xl" className="lg:!w-12 lg:!h-12" delay={560 + i * 80} />
                                            <div className="flex-1 space-y-2">
                                                <Bone className="h-4 w-28 rounded" delay={600 + i * 80} />
                                                <Bone className="h-3 w-20 rounded" delay={640 + i * 80} />
                                            </div>
                                            <div className="flex flex-col items-end space-y-1.5">
                                                <Bone className="h-4 w-16 rounded" delay={660 + i * 80} />
                                                <Bone className="h-3 w-12 rounded" delay={700 + i * 80} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right Column – desktop only */}
                        <div className="hidden lg:block lg:col-span-2 space-y-6">
                            {/* Wallet Details */}
                            <div
                                className="p-5 rounded-2xl"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(30,58,95,0.5), rgba(20,40,70,0.7))',
                                    border: '1px solid rgba(99,102,241,0.2)',
                                }}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <Bone className="h-4 w-28 rounded" delay={300} />
                                    <BoneIcon size={32} radius="rounded-lg" delay={340} />
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <Bone className="h-3 w-20 rounded mb-1.5" delay={380} />
                                        <Bone className="h-5 w-28 rounded" delay={410} />
                                    </div>
                                    <div>
                                        <Bone className="h-3 w-24 rounded mb-1.5" delay={440} />
                                        <Bone className="h-4 w-full rounded" delay={470} />
                                    </div>
                                    <div>
                                        <Bone className="h-3 w-20 rounded mb-1.5" delay={500} />
                                        <div className="flex items-center gap-2">
                                            <Bone className="flex-1 h-2 rounded-full" delay={530} />
                                            <Bone className="h-3.5 w-10 rounded" delay={550} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Monthly Stats */}
                            <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/10">
                                <Bone className="h-4 w-24 rounded mb-4" delay={560} />
                                <div className="space-y-4">
                                    {[0, 1].map((i) => (
                                        <div key={i} className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <BoneIcon size={32} radius="rounded-lg" delay={600 + i * 80} />
                                                <Bone className="h-3.5 w-16 rounded" delay={640 + i * 80} />
                                            </div>
                                            <Bone className="h-4 w-16 rounded" delay={680 + i * 80} />
                                        </div>
                                    ))}
                                    <div className="pt-3 border-t border-white/10 flex items-center justify-between">
                                        <Bone className="h-3.5 w-20 rounded" delay={780} />
                                        <Bone className="h-4 w-14 rounded" delay={810} />
                                    </div>
                                </div>
                            </div>

                            {/* Wallet Level */}
                            <div className="p-5 rounded-2xl bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/20">
                                <div className="flex items-center justify-between mb-3">
                                    <Bone className="h-4 w-24 rounded" delay={840} />
                                    <Bone className="h-5 w-16 rounded" delay={870} />
                                </div>
                                <Bone className="h-2 w-full rounded-full mb-2" delay={900} />
                                <Bone className="h-3 w-40 rounded" delay={930} />
                            </div>
                        </div>
                    </div>

                    {/* Mobile Wallet Info */}
                    <div className="lg:hidden mt-5">
                        <div
                            className="p-4 rounded-2xl"
                            style={{
                                background: 'linear-gradient(135deg, rgba(30,58,95,0.5), rgba(20,40,70,0.7))',
                                border: '1px solid rgba(99,102,241,0.2)',
                            }}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <BoneIcon size={44} radius="rounded-xl" delay={600} />
                                    <div className="space-y-2">
                                        <Bone className="h-3.5 w-24 rounded" delay={640} />
                                        <Bone className="h-3 w-36 rounded" delay={680} />
                                    </div>
                                </div>
                                <BoneIcon size={36} radius="rounded-xl" delay={720} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Wrap>
    )
}

// ─── History Skeleton ───────────────────────────────────────────────────────────

export function HistorySkeleton() {
    return (
        <Wrap>
            {/* Header */}
            <header className="sticky top-0 z-40 px-4 lg:px-8 py-3 bg-[#0a0a0f]/95 backdrop-blur-xl border-b border-white/5">
                <div className="max-w-6xl mx-auto">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                            <BoneIcon size={40} radius="rounded-xl" />
                            <div className="space-y-1.5">
                                <Bone className="h-5 w-24 rounded" delay={50} />
                                <Bone className="h-3 w-32 rounded" delay={80} />
                            </div>
                        </div>
                        <BoneIcon size={36} radius="rounded-xl" delay={100} />
                    </div>
                    <Bone className="h-10 w-full rounded-xl mb-3" delay={130} />
                    <div className="flex gap-1.5">
                        <Bone className="flex-1 h-8 rounded-lg" delay={160} />
                        <Bone className="flex-1 h-8 rounded-lg" delay={180} />
                        <Bone className="flex-1 h-8 rounded-lg" delay={200} />
                    </div>
                </div>
            </header>

            <div className="px-4 lg:px-8 py-4 lg:py-6">
                <div className="max-w-6xl mx-auto">
                    <div className="lg:grid lg:grid-cols-5 xl:grid-cols-7 lg:gap-8">
                        {/* Transaction List */}
                        <div className="lg:col-span-3 xl:col-span-4">
                            <div className="rounded-2xl overflow-hidden bg-white/[0.02] border border-white/[0.06]">
                                {/* Date group header */}
                                <div className="px-4 py-2 bg-white/[0.01]">
                                    <Bone className="h-3 w-16 rounded" delay={230} />
                                </div>
                                {[0, 1, 2, 3, 4, 5].map((i) => (
                                    <div key={i} className={`flex items-center gap-3 px-4 py-3.5 ${i < 5 ? 'border-b border-white/[0.04]' : ''}`}>
                                        <BoneIcon size={38} radius="rounded-xl" delay={260 + i * 50} />
                                        <div className="flex-1 space-y-1.5">
                                            <Bone className="h-3.5 w-28 rounded" delay={280 + i * 50} />
                                            <Bone className="h-3 w-36 rounded" delay={300 + i * 50} />
                                        </div>
                                        <div className="flex flex-col items-end space-y-1">
                                            <Bone className="h-3.5 w-14 rounded" delay={320 + i * 50} />
                                            <Bone className="h-2.5 w-10 rounded" delay={340 + i * 50} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Sidebar */}
                        <div className="hidden lg:block lg:col-span-2 xl:col-span-3 space-y-5">
                            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
                                <Bone className="h-3 w-20 rounded mb-4" delay={400} />
                                <div className="space-y-4">
                                    {[0, 1].map((i) => (
                                        <div key={i} className="flex items-center gap-3">
                                            <BoneIcon size={32} radius="rounded-lg" delay={430 + i * 60} />
                                            <Bone className="flex-1 h-3 rounded" delay={450 + i * 60} />
                                            <Bone className="h-4 w-16 rounded" delay={470 + i * 60} />
                                        </div>
                                    ))}
                                    <div className="pt-3 border-t border-white/[0.05] flex justify-between">
                                        <Bone className="h-3 w-14 rounded" delay={560} />
                                        <Bone className="h-4 w-12 rounded" delay={580} />
                                    </div>
                                </div>
                            </div>
                            <div className="p-5 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.06), rgba(139,92,246,0.04))', border: '1px solid rgba(99,102,241,0.1)' }}>
                                <Bone className="h-3 w-16 rounded mb-4" delay={600} />
                                <div className="space-y-3">
                                    {[0, 1].map((i) => (
                                        <div key={i} className="flex items-center justify-between">
                                            <div className="flex items-center gap-2.5">
                                                <BoneIcon size={28} radius="rounded-lg" delay={630 + i * 40} />
                                                <Bone className="h-3 w-18 rounded" delay={650 + i * 40} />
                                            </div>
                                            <Bone className="h-3.5 w-12 rounded" delay={670 + i * 40} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Wrap>
    )
}

// ─── Receive Skeleton ───────────────────────────────────────────────────────────

export function ReceiveSkeleton() {
    return (
        <Wrap>
            {/* Header */}
            <header className="sticky top-0 z-40 px-5 lg:px-8 py-3 bg-[#0a0a0f]/95 backdrop-blur-xl border-b border-white/5">
                <div className="max-w-6xl mx-auto flex items-center gap-3">
                    <BoneIcon size={36} radius="rounded-xl" delay={50} />
                    <div className="flex-1 space-y-1.5">
                        <Bone className="h-5 lg:h-6 w-20 rounded" delay={80} />
                        <Bone className="h-3 w-48 rounded" delay={120} />
                    </div>
                    {/* Desktop network badge */}
                    <Bone className="hidden lg:block h-7 w-28 rounded-full" delay={140} />
                    <Bone
                        className="h-9 lg:h-10 w-20 lg:w-24 rounded-xl flex-shrink-0"
                        delay={160}
                        style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.2), rgba(124,58,237,0.15))' }}
                    />
                </div>
            </header>

            <div className="px-4 lg:px-8 py-5 lg:py-8">
                <div className="max-w-6xl mx-auto">
                    <div className="lg:grid lg:grid-cols-5 xl:grid-cols-7 lg:gap-8">
                        {/* Left: Main content */}
                        <div className="lg:col-span-3 xl:col-span-4 space-y-5 lg:space-y-6">
                            {/* Profile Card */}
                            <div className="rounded-2xl p-5 lg:p-6 border border-white/[0.08] bg-white/[0.03]">
                                <div className="flex items-center gap-4 lg:gap-5">
                                    <BoneIcon size={64} radius="rounded-2xl" className="lg:!w-20 lg:!h-20 lg:!rounded-3xl" delay={200} />
                                    <div className="flex-1 space-y-2.5">
                                        <Bone className="h-5 lg:h-6 w-40 rounded" delay={240} />
                                        <Bone className="h-7 w-28 rounded-full" delay={280} />
                                    </div>
                                    <div className="hidden lg:flex items-center gap-2">
                                        <BoneIcon size={36} radius="rounded-xl" delay={300} />
                                        <BoneIcon size={36} radius="rounded-xl" delay={320} />
                                    </div>
                                </div>
                            </div>

                            {/* Tab Switcher */}
                            <div className="flex gap-1.5 p-1 bg-white/[0.04] rounded-xl border border-white/[0.06]">
                                <Bone
                                    className="flex-1 h-10 lg:h-12 rounded-lg"
                                    delay={340}
                                    style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(124,58,237,0.1))' }}
                                />
                                <Bone className="flex-1 h-10 lg:h-12 rounded-lg" delay={380} />
                            </div>

                            {/* QR Code Card */}
                            <div className="rounded-2xl p-6 lg:p-8 border border-white/[0.08] bg-white/[0.03] flex flex-col items-center">
                                <div className="p-4 bg-white/[0.06] rounded-2xl mb-5">
                                    <BoneIcon size={240} radius="rounded-xl" delay={420} />
                                </div>
                                <Bone className="h-3.5 w-52 rounded" delay={470} />
                            </div>

                            {/* Copy Cards */}
                            <div className="grid grid-cols-2 gap-3 lg:gap-4">
                                <div
                                    className="p-4 lg:p-5 rounded-2xl"
                                    style={{ background: 'rgba(168,85,247,0.04)', border: '1px solid rgba(168,85,247,0.1)' }}
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <BoneIcon size={32} radius="rounded-lg" delay={500} />
                                        <BoneIcon size={16} radius="rounded" delay={520} />
                                    </div>
                                    <Bone className="h-3 w-16 rounded mb-1.5" delay={540} />
                                    <Bone className="h-4 w-24 rounded" delay={560} />
                                </div>
                                <div
                                    className="p-4 lg:p-5 rounded-2xl"
                                    style={{ background: 'rgba(6,182,212,0.04)', border: '1px solid rgba(6,182,212,0.1)' }}
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <BoneIcon size={32} radius="rounded-lg" delay={530} />
                                        <BoneIcon size={16} radius="rounded" delay={550} />
                                    </div>
                                    <Bone className="h-3 w-20 rounded mb-1.5" delay={570} />
                                    <Bone className="h-4 w-20 rounded" delay={590} />
                                </div>
                            </div>

                            {/* Mobile instructions */}
                            <div
                                className="lg:hidden rounded-2xl p-5"
                                style={{ background: 'rgba(99,102,241,0.03)', border: '1px solid rgba(99,102,241,0.08)' }}
                            >
                                <div className="flex items-center gap-2 mb-3">
                                    <BoneIcon size={16} radius="rounded" delay={600} />
                                    <Bone className="h-3.5 w-28 rounded" delay={620} />
                                </div>
                                {[0, 1, 2].map((i) => (
                                    <Bone key={i} className="h-3 w-full rounded mb-2" delay={640 + i * 30} />
                                ))}
                            </div>
                        </div>

                        {/* Right: Sidebar (Desktop only) */}
                        <div className="hidden lg:block lg:col-span-2 xl:col-span-3">
                            <div className="sticky top-24 space-y-5">
                                {/* How to Receive */}
                                <div className="rounded-2xl p-5 lg:p-6 border border-white/[0.08] bg-white/[0.03]">
                                    <div className="flex items-center gap-2.5 mb-5">
                                        <BoneIcon size={28} radius="rounded-lg" delay={300} />
                                        <Bone className="h-4 w-28 rounded" delay={320} />
                                    </div>
                                    {[0, 1, 2].map((i) => (
                                        <div key={i} className="flex items-start gap-3 mb-4 last:mb-0">
                                            <BoneIcon size={36} radius="rounded-xl" delay={340 + i * 50} />
                                            <div className="flex-1 space-y-1.5 pt-1">
                                                <Bone className="h-3.5 w-28 rounded" delay={370 + i * 50} />
                                                <Bone className="h-2.5 w-full rounded" delay={400 + i * 50} />
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Balance Card */}
                                <div className="rounded-2xl p-5 border border-white/[0.08] bg-white/[0.03]">
                                    <div className="flex items-center gap-2 mb-4">
                                        <BoneIcon size={16} radius="rounded" delay={500} />
                                        <Bone className="h-3.5 w-24 rounded" delay={520} />
                                    </div>
                                    <div className="space-y-3">
                                        {[0, 1].map((i) => (
                                            <div key={i} className="flex items-center justify-between">
                                                <div className="flex items-center gap-2.5">
                                                    <BoneIcon size={28} radius="rounded-lg" delay={540 + i * 50} />
                                                    <Bone className="h-3.5 w-20 rounded" delay={570 + i * 50} />
                                                </div>
                                                <Bone className="h-4 w-14 rounded" delay={590 + i * 50} />
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-4 pt-3 border-t border-white/5 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Bone className="h-2.5 w-20 rounded" delay={680} />
                                            <div className="flex items-center gap-1.5">
                                                <Bone className="h-1.5 w-16 rounded-full" delay={700} />
                                                <Bone className="h-3 w-8 rounded" delay={720} />
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <Bone className="h-2.5 w-20 rounded" delay={740} />
                                            <Bone className="h-3 w-8 rounded" delay={760} />
                                        </div>
                                    </div>
                                </div>

                                {/* Security Notice */}
                                <div
                                    className="rounded-xl p-4"
                                    style={{ background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.08)' }}
                                >
                                    <div className="flex items-start gap-3">
                                        <BoneIcon size={16} radius="rounded" delay={780} />
                                        <div className="flex-1 space-y-2">
                                            <Bone className="h-3 w-28 rounded" delay={800} />
                                            <Bone className="h-2.5 w-full rounded" delay={820} />
                                            <Bone className="h-2.5 w-3/4 rounded" delay={840} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Wrap>
    )
}

// ─── Settings Skeleton ──────────────────────────────────────────────────────────

export function SettingsSkeleton() {
    return (
        <Wrap>
            {/* Header */}
            <header className="sticky top-0 z-40 px-5 lg:px-8 pt-4 pb-3 bg-[#0a0a0f]/95 backdrop-blur-md">
                <div className="max-w-5xl mx-auto flex items-center gap-3">
                    <BoneIcon size={40} radius="rounded-xl" className="lg:hidden" />
                    <div className="space-y-2">
                        <Bone className="h-5 lg:h-6 w-20 rounded" delay={50} />
                        <Bone className="h-3 w-32 lg:w-36 rounded" delay={100} />
                    </div>
                </div>
            </header>

            <div className="px-5 lg:px-8 py-2 lg:py-4">
                <div className="max-w-5xl mx-auto">
                    <div className="lg:grid lg:grid-cols-5 lg:gap-8 space-y-5 lg:space-y-0">
                        {/* Left – Profile + Account + Security */}
                        <div className="lg:col-span-3 space-y-5">
                            {/* Profile Card */}
                            <div
                                className="p-5 lg:p-6 rounded-2xl"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(139,92,246,0.08))',
                                    border: '1px solid rgba(99,102,241,0.15)',
                                }}
                            >
                                <div className="flex items-center gap-4">
                                    <BoneIcon size={64} radius="rounded-2xl" className="lg:!w-20 lg:!h-20" delay={150} />
                                    <div className="flex-1 space-y-2">
                                        <Bone className="h-5 lg:h-6 w-40 rounded" delay={200} />
                                        <Bone className="h-3.5 w-24 rounded" delay={250} />
                                        <div className="flex items-center gap-2 mt-1">
                                            <Bone className="h-5 w-16 rounded-full" delay={300} />
                                            <Bone className="h-5 w-20 rounded-full" delay={340} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Account Section */}
                            <div>
                                <Bone className="h-3 w-16 rounded mb-2 ml-1" delay={380} />
                                <div className="rounded-2xl overflow-hidden bg-white/[0.03] border border-white/10 divide-y divide-white/5">
                                    <BoneListRow delay={410} />
                                    <BoneListRow delay={460} />
                                </div>
                            </div>

                            {/* Security Section */}
                            <div>
                                <Bone className="h-3 w-16 rounded mb-2 ml-1" delay={500} />
                                <div className="rounded-2xl overflow-hidden bg-white/[0.03] border border-white/10 divide-y divide-white/5">
                                    <BoneListRow delay={530} />
                                    <BoneListRow delay={580} />
                                    <BoneListRow delay={630} />
                                </div>
                            </div>
                        </div>

                        {/* Right – Preferences + Actions */}
                        <div className="lg:col-span-2 space-y-5">
                            {/* Preferences */}
                            <div>
                                <Bone className="h-3 w-20 rounded mb-2 ml-1" delay={500} />
                                <div className="rounded-2xl overflow-hidden bg-white/[0.03] border border-white/10 divide-y divide-white/5">
                                    <BoneListRow delay={530} />
                                    <BoneListRow delay={580} />
                                    <BoneListRow delay={630} />
                                </div>
                            </div>

                            {/* Sign Out */}
                            <Bone
                                className="h-14 w-full rounded-2xl"
                                delay={680}
                                style={{
                                    background: 'rgba(239,68,68,0.08)',
                                    border: '1px solid rgba(239,68,68,0.12)',
                                }}
                            />

                            {/* Version */}
                            <Bone className="h-3 w-28 rounded mx-auto" delay={720} />
                        </div>
                    </div>
                </div>
            </div>
        </Wrap>
    )
}

// ─── Send Skeleton ──────────────────────────────────────────────────────────────

export function SendSkeleton() {
    return (
        <Wrap>
            {/* Header */}
            <header className="sticky top-0 z-40 px-5 lg:px-8 py-3 bg-[#0a0a0f]/95 backdrop-blur-xl border-b border-white/5">
                <div className="max-w-6xl mx-auto flex items-center gap-3">
                    <BoneIcon size={36} radius="rounded-xl" delay={50} />
                    <div className="flex-1 space-y-1.5">
                        <Bone className="h-5 lg:h-6 w-12 rounded" delay={80} />
                        <Bone className="h-3 w-44 rounded" delay={120} />
                    </div>
                    {/* Desktop step indicator skeleton */}
                    <div className="hidden lg:flex items-center gap-0">
                        {[0, 1, 2, 3].map((i) => (
                            <div key={i} className="flex items-center">
                                <div className="flex flex-col items-center">
                                    <BoneIcon size={40} radius="rounded-full" delay={200 + i * 50} />
                                    <Bone className="h-2.5 w-12 rounded mt-1.5" delay={220 + i * 50} />
                                </div>
                                {i < 3 && <Bone className="w-16 xl:w-24 h-px mx-2 mb-5" delay={240 + i * 50} />}
                            </div>
                        ))}
                    </div>
                    <Bone
                        className="h-7 w-20 rounded-full flex-shrink-0"
                        delay={160}
                        style={{ background: 'rgba(16,185,129,0.12)' }}
                    />
                </div>
            </header>

            {/* Content */}
            <div className="px-4 lg:px-8 py-5 lg:py-8">
                <div className="max-w-6xl mx-auto">
                    {/* Mobile progress bar */}
                    <div className="lg:hidden flex gap-2 mb-4 px-1">
                        {[0, 1, 2, 3].map((i) => (
                            <Bone key={i} className="flex-1 h-1 rounded-full" delay={180 + i * 30} />
                        ))}
                    </div>

                    {/* Desktop: Two-column layout */}
                    <div className="lg:grid lg:grid-cols-5 xl:grid-cols-7 lg:gap-8">
                        {/* Left: Form cards */}
                        <div className="lg:col-span-3 xl:col-span-4 space-y-5 lg:space-y-6">
                            {/* Recipient Card */}
                            <div className="rounded-2xl p-5 lg:p-6 border border-white/[0.08] bg-white/[0.03]">
                                <div className="flex items-center gap-2 mb-4">
                                    <BoneIcon size={28} radius="rounded-lg" delay={200} />
                                    <Bone className="h-4 w-20 rounded" delay={230} />
                                </div>
                                <Bone className="h-12 lg:h-14 w-full rounded-xl" delay={260} />
                            </div>

                            {/* Currency Card */}
                            <div className="rounded-2xl p-5 lg:p-6 border border-white/[0.08] bg-white/[0.03]">
                                <div className="flex items-center gap-2 mb-4">
                                    <BoneIcon size={28} radius="rounded-lg" delay={300} />
                                    <Bone className="h-4 w-16 rounded" delay={330} />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div
                                        className="p-4 lg:p-5 rounded-xl"
                                        style={{
                                            background: 'rgba(139,92,246,0.08)',
                                            border: '1px solid rgba(139,92,246,0.15)',
                                        }}
                                    >
                                        <div className="flex items-center gap-2.5 mb-2">
                                            <BoneIcon size={20} radius="rounded" delay={360} />
                                            <Bone className="h-4 w-20 rounded" delay={380} />
                                        </div>
                                        <Bone className="h-3.5 w-24 rounded" delay={400} />
                                    </div>
                                    <div className="p-4 lg:p-5 rounded-xl bg-white/[0.02] border border-white/[0.08]">
                                        <div className="flex items-center gap-2.5 mb-2">
                                            <BoneIcon size={20} radius="rounded" delay={390} />
                                            <Bone className="h-4 w-16 rounded" delay={410} />
                                        </div>
                                        <Bone className="h-3.5 w-24 rounded" delay={430} />
                                    </div>
                                </div>
                            </div>

                            {/* Amount Card */}
                            <div className="rounded-2xl p-5 lg:p-6 border border-white/[0.08] bg-white/[0.03]">
                                <div className="flex items-center gap-2 mb-4">
                                    <BoneIcon size={28} radius="rounded-lg" delay={440} />
                                    <Bone className="h-4 w-14 rounded" delay={460} />
                                </div>
                                <Bone className="h-14 lg:h-16 w-full rounded-xl mb-4" delay={480} />
                                <div className="flex gap-2 flex-wrap">
                                    {[0, 1, 2, 3, 4, 5].map((i) => (
                                        <Bone key={i} className="h-9 w-16 rounded-lg" delay={510 + i * 25} />
                                    ))}
                                </div>
                            </div>

                            {/* Memo Card */}
                            <div className="rounded-2xl p-5 lg:p-6 border border-white/[0.08] bg-white/[0.03]">
                                <div className="flex items-center gap-2 mb-3">
                                    <BoneIcon size={28} radius="rounded-lg" delay={660} />
                                    <Bone className="h-4 w-24 rounded" delay={680} />
                                </div>
                                <Bone className="h-11 w-full rounded-xl" delay={700} />
                            </div>

                            {/* Submit Button */}
                            <Bone
                                className="h-14 lg:h-16 w-full rounded-xl"
                                delay={730}
                                style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(124,58,237,0.1))' }}
                            />
                        </div>

                        {/* Right: Summary sidebar (Desktop only) */}
                        <div className="hidden lg:block lg:col-span-2 xl:col-span-3">
                            <div className="sticky top-24 space-y-5">
                                {/* Balance Card */}
                                <div className="rounded-2xl p-5 border border-white/[0.08] bg-white/[0.03]">
                                    <div className="flex items-center gap-2 mb-4">
                                        <BoneIcon size={16} radius="rounded" delay={300} />
                                        <Bone className="h-3.5 w-24 rounded" delay={320} />
                                    </div>
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <BoneIcon size={28} radius="rounded-lg" delay={340} />
                                            <Bone className="h-3.5 w-20 rounded" delay={360} />
                                        </div>
                                        <Bone className="h-5 w-16 rounded" delay={380} />
                                    </div>
                                </div>

                                {/* Preview Card */}
                                <div className="rounded-2xl p-5 border border-white/[0.08] bg-white/[0.03]">
                                    <div className="flex items-center gap-2 mb-4">
                                        <BoneIcon size={16} radius="rounded" delay={400} />
                                        <Bone className="h-3.5 w-32 rounded" delay={420} />
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <Bone className="h-2.5 w-16 rounded mb-2" delay={440} />
                                            <Bone className="h-4 w-28 rounded" delay={460} />
                                        </div>
                                        <div>
                                            <Bone className="h-2.5 w-14 rounded mb-2" delay={480} />
                                            <Bone className="h-4 w-20 rounded" delay={500} />
                                        </div>
                                    </div>
                                    <div className="mt-4 pt-3 border-t border-white/5">
                                        <div className="flex items-center gap-2">
                                            <BoneIcon size={8} radius="rounded-full" delay={520} />
                                            <Bone className="h-3 w-36 rounded" delay={540} />
                                        </div>
                                    </div>
                                </div>

                                {/* Security Notice */}
                                <div
                                    className="rounded-xl p-4"
                                    style={{ background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.08)' }}
                                >
                                    <div className="flex items-start gap-3">
                                        <BoneIcon size={16} radius="rounded" delay={560} />
                                        <div className="flex-1 space-y-2">
                                            <Bone className="h-3 w-24 rounded" delay={580} />
                                            <Bone className="h-2.5 w-full rounded" delay={600} />
                                            <Bone className="h-2.5 w-3/4 rounded" delay={620} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Wrap>
    )
}
